import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { getErrorMessage } from "@/utilities";
import { StoreSeller, storePopulationsApi } from "@/services/store-populations.api";
import { AgentKnowledge, Knowledge, KNOWLEDGE_SECTIONS, agentApi } from "./api";
import {
  SECCIONES,
  conocimientoCambio,
  fecha,
  normalizarConocimiento,
  notasExcedidas,
  seccionesExcedidas,
} from "./agent-config";
import { OpcionDeAsistente } from "./asistentes";

const { Text, Paragraph } = Typography;
const TOPE_POR_DEFECTO = 4000;

interface Props {
  storeCode: string;
  /** El usuario asistente (D62): todo el conocimiento es suyo. */
  userId: number;
  editable: boolean;
  /** Los otros asistentes del store, para «Copiar conocimiento de…». */
  otros?: OpcionDeAsistente[];
  onGuardado?: () => void;
}

/**
 * El conocimiento de un asistente (tareas 5.5 y 5.11, D46 y D62): seis secciones con su guía y su
 * contador, que se guardan juntas y dejan una versión; el historial, desde donde se restaura una
 * anterior; y copiar el de otro asistente del store, que también deja una versión.
 */
export const ConocimientoTab: React.FC<Props> = ({ storeCode, userId, editable, otros = [], onGuardado }) => {
  const [datos, setDatos] = useState<AgentKnowledge | null>(null);
  const [borrador, setBorrador] = useState<Knowledge>({});
  const [saving, setSaving] = useState(false);
  const [restaurando, setRestaurando] = useState<number | null>(null);
  const [fuente, setFuente] = useState<number | null>(null);
  const [copiando, setCopiando] = useState(false);
  // D58: las sucursales del store, para sus notas. Con una sola no hace falta: es el store.
  const [sellers, setSellers] = useState<StoreSeller[]>([]);

  const aplicar = (d: AgentKnowledge) => {
    setDatos(d);
    setBorrador(d.knowledge);
  };

  useEffect(() => {
    agentApi
      .conocimiento(storeCode, userId)
      .then(aplicar)
      .catch((e) => message.error(getErrorMessage(e)));
    storePopulationsApi
      .listSellers(storeCode)
      .then(setSellers)
      .catch(() => setSellers([]));
  }, [storeCode, userId]);

  const topes =
    datos?.sections ?? KNOWLEDGE_SECTIONS.map((key) => ({ key, maxLength: TOPE_POR_DEFECTO }));
  const excedidas = seccionesExcedidas(borrador, topes);
  const notasLargas = notasExcedidas(borrador, TOPE_POR_DEFECTO);
  const cambio = !!datos && conocimientoCambio(datos.knowledge, borrador);

  const guardar = async () => {
    setSaving(true);
    try {
      aplicar(await agentApi.guardarConocimiento(storeCode, userId, normalizarConocimiento(borrador)));
      onGuardado?.();
      message.success("Conocimiento guardado. El asistente lo toma en menos de un minuto.");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const restaurar = async (version: number) => {
    setRestaurando(version);
    try {
      aplicar(await agentApi.restaurar(storeCode, userId, version));
      onGuardado?.();
      message.success(`Se restauró la versión ${version}`);
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setRestaurando(null);
    }
  };

  const nombreDeFuente = otros.find((o) => o.value === fuente)?.label ?? "otro asistente";

  const copiar = async () => {
    if (!fuente) return;
    setCopiando(true);
    try {
      aplicar(await agentApi.copiarConocimiento(storeCode, userId, fuente));
      onGuardado?.();
      setFuente(null);
      message.success(`Se copió el conocimiento de ${nombreDeFuente}`);
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setCopiando(false);
    }
  };

  return (
    <Flex vertical gap={16}>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Lo que este asistente sabe del Store, además de las reglas de xcons, que no se pueden cambiar.
        Escribilo como se lo explicarías a un vendedor nuevo. Los precios y el stock no van acá: los
        consulta siempre en el momento.
      </Paragraph>

      {editable && otros.length > 0 && (
        <Flex gap={8} wrap="wrap" align="center">
          <Select
            style={{ minWidth: 260, flex: 1, maxWidth: 400 }}
            placeholder="Copiar conocimiento de…"
            value={fuente ?? undefined}
            onChange={(v) => setFuente(v ?? null)}
            allowClear
            options={otros}
          />
          <Popconfirm
            title={`¿Copiar el conocimiento de ${nombreDeFuente}?`}
            description={
              cambio
                ? "Reemplaza el de este asistente y se pierden los cambios sin guardar. Queda como una versión nueva; la API key no se copia."
                : "Reemplaza el de este asistente y queda como una versión nueva; la actual sigue en el historial. La API key no se copia."
            }
            okText="Copiar"
            cancelText="Cancelar"
            onConfirm={copiar}
            disabled={!fuente}
          >
            <Button icon={<CopyOutlined />} disabled={!fuente} loading={copiando}>
              Copiar
            </Button>
          </Popconfirm>
        </Flex>
      )}

      {topes.map(({ key, maxLength }) => (
        <Card key={key} size="small" title={SECCIONES[key]?.titulo ?? key}>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            {SECCIONES[key]?.guia}
          </Text>
          <Input.TextArea
            value={borrador[key] ?? ""}
            onChange={(e) => setBorrador((b) => ({ ...b, [key]: e.target.value }))}
            readOnly={!editable}
            autoSize={{ minRows: 4, maxRows: 18 }}
            showCount
            count={{ max: maxLength, strategy: (txt) => txt.trim().length }}
            status={excedidas.includes(key) ? "error" : undefined}
          />
        </Card>
      ))}

      {sellers.length > 1 && (
        <Card size="small" title="Notas de la sucursal">
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Para los canales que venden por una sucursal. La dirección, los horarios, los días que no
            atiende, los medios de pago y las formas de entrega los toma solo de la configuración del
            seller: acá va lo que la configuración no dice, como zonas y costos de envío, barrios que
            cubre o mínimos de compra.
          </Text>
          <Flex vertical gap={12}>
            {sellers.map((s) => (
              <Flex key={s.sellerCode} vertical gap={4}>
                <Text strong>{s.name}</Text>
                <Input.TextArea
                  value={borrador.branchNotes?.[s.sellerCode] ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => ({
                      ...b,
                      branchNotes: { ...(b.branchNotes ?? {}), [s.sellerCode]: e.target.value },
                    }))
                  }
                  readOnly={!editable}
                  autoSize={{ minRows: 2, maxRows: 12 }}
                  showCount
                  count={{ max: TOPE_POR_DEFECTO, strategy: (txt) => txt.trim().length }}
                  status={notasLargas.includes(s.sellerCode) ? "error" : undefined}
                />
              </Flex>
            ))}
          </Flex>
        </Card>
      )}

      {(excedidas.length > 0 || notasLargas.length > 0) && (
        <Alert
          type="error"
          showIcon
          message={`Hay que acortar ${[
            ...excedidas.map((k) => SECCIONES[k]?.titulo ?? k),
            ...notasLargas.map((c) => `las notas de ${sellers.find((s) => s.sellerCode === c)?.name ?? c}`),
          ].join(", ")} para poder guardar`}
        />
      )}

      {editable && (
        <Flex justify="flex-end" align="center" gap={12}>
          {cambio && <Text type="secondary">Hay cambios sin guardar</Text>}
          <Button
            type="primary"
            icon={<CheckOutlined />}
            iconPosition="end"
            loading={saving}
            disabled={!cambio || excedidas.length > 0 || notasLargas.length > 0}
            onClick={guardar}
          >
            Guardar
          </Button>
        </Flex>
      )}

      <Card size="small" title="Versiones">
        <Table
          size="small"
          rowKey="version"
          dataSource={datos?.versions ?? []}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: "Todavía no se guardó ninguna versión" }}
          columns={[
            {
              title: "Versión",
              render: (_, v) => (
                <Flex gap={8} align="center">
                  {v.version}
                  {v.version === datos?.version && <Tag color="green">Actual</Tag>}
                </Flex>
              ),
            },
            {
              title: "Fecha",
              render: (_, v) => fecha(v.createdAt),
            },
            {
              title: "Origen",
              render: (_, v) => (v.restoredFrom ? `Restaurada de la ${v.restoredFrom}` : "Editada"),
            },
            {
              title: "",
              align: "right",
              render: (_, v) =>
                editable && v.version !== datos?.version ? (
                  <Popconfirm
                    title={`¿Volver a la versión ${v.version}?`}
                    description={
                      cambio
                        ? "Se pierden los cambios sin guardar. Queda como una versión nueva."
                        : "Queda como una versión nueva; la actual sigue en el historial."
                    }
                    okText="Restaurar"
                    cancelText="Cancelar"
                    onConfirm={() => restaurar(v.version)}
                  >
                    <Button size="small" loading={restaurando === v.version}>
                      Restaurar
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      </Card>
    </Flex>
  );
};
